import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";

// formik validation
import * as Yup from "yup";
import { useFormik } from "formik";
import CustomInput from "components/CommonInputs/CustomInput";
import { useNavigate, useParams } from "react-router-dom";
import { get, post, put } from "helpers/api_helper";
import * as api from "../../constants/api_constants";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import Select from "react-select";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import Footer from "components/VerticalLayout/Footer";
import { charactersOnly } from "helpers/validations/regex";
import { charactersWithSpaces } from "helpers/validations/regex";
import { showErrorToast, showSuccessToast } from "helpers/toastMessages";

function AddUser() {
  const { userId } = useParams(); // Get userId from URL params
  const navigate = useNavigate();
  const [roleSelect, setRoleSelect] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [initialValues, setInitialValues] = useState({
    employeeId: null,
    subscriptionId: "",
    firstname: "",
    lastname: "",
    username: "",
    email: "",
    password: "",
    phone: "",
    role: [],
    companyName: "",
    status: 1,
    description: "",
  });

  // Fetch user data if editing
  useEffect(() => {
    fetchData();
    employeeData();
    if (userId) {
      fetchUserData(userId);
    }
  }, [userId]);

  const handleEmployeeChange = (e) => {
    const selectedId = parseInt(e.target.value);

    const selectedManager = employees.find(
      (employee) => employee.value === selectedId
    );
    validation.setFieldValue(
      "firstname",
      selectedManager ? selectedManager.firstName : ""
    );

    validation.setFieldValue(
      "lastname",
      selectedManager ? selectedManager.lastName : ""
    );

    validation.setFieldValue(
      "email",
      selectedManager ? selectedManager.email : ""
    );

    validation.setFieldValue(
      "employeeId",
      selectedManager ? selectedManager.value : ""
    );
  };

  const employeeData = async () => {
    try {
      const response = await get(api.GET_ALL_EMPLOYEES_NAME_ID_MAIL);
      const employeeResponse = response.map((item) => ({
        value: item.employeeId,
        label: `${item.firstName}  ${item.lastName}`,
        firstName: item.firstName,
        lastName: item.lastName,
        number: item.number,
        email: item.email,
      }));
      setEmployees(employeeResponse);
    } catch (error) {
      console.log("Error fetching employees data");
    }
  };

  const fetchData = async () => {
    try {
      const fetchRolesData = await get(api.GET_ROLES_DATA);
      const rolesData = fetchRolesData.rolePermissionPage.content;

      const formattedRoles = rolesData.map((role) => ({
        value: role.id,
        label: role.role,
      }));
      setRoleSelect(formattedRoles);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const fetchUserData = async (id) => {
    try {
      const userData = await get(api.GET_USERDATA_BY_ID(id));

      setInitialValues({
        employeeId: userData.employeeId || null,
        subscriptionId: userData.subscriptionId || null,
        firstname: userData.firstName,
        lastname: userData.lastName,
        username: userData.userName,
        email: userData.email,
        phone: userData.phoneNumber,
        role: userData.rolePermissions || [],
        companyName: userData.companyName,
        status: userData.status,
        description: userData.description,
      });
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const validation = useFormik({
    enableReinitialize: true, // Reinitialize form with fetched data
    initialValues: initialValues,
    validationSchema: Yup.object({
      employeeId: Yup.number().notRequired(),
      subscriptionId: Yup.number("Should be Number").notRequired(),
      firstname: Yup.string()
        .required("Please enter first name")
        .matches(charactersOnly, "First Name should contain only characters"),
      lastname: Yup.string()
        .required("Please enter last name")
        .matches(charactersOnly, "Last Name should contain only characters"),
      username: Yup.string()
        .required("Please enter user name")
        .matches(
          charactersWithSpaces,
          "username should contain only characters"
        ),
      email: Yup.string()
        .email("Must be a valid email")
        .required("Please enter email"),
      password: userId
        ? Yup.string() // If editing, password is optional
            .min(8, "Password should be at least 8 characters")
            .matches(
              /^(?=.*[a-z])/,
              "Must contain at least one lowercase letter"
            )
            .matches(
              /^(?=.*[A-Z])/,
              "Must contain at least one uppercase letter"
            )
            .matches(/^(?=.*\d)/, "Must contain at least one number")
            .matches(
              /^(?=.*[!@#$%^&*()\-_=+{};:,<.>])/,
              "Must contain at least one special character"
            )
            .notRequired()
        : Yup.string() // If creating, password is required
            .required("Password is required")
            .min(8, "Password should be at least 8 characters")
            .matches(
              /^(?=.*[a-z])/,
              "Must contain at least one lowercase letter"
            )
            .matches(
              /^(?=.*[A-Z])/,
              "Must contain at least one uppercase letter"
            )
            .matches(/^(?=.*\d)/, "Must contain at least one number")
            .matches(
              /^(?=.*[!@#$%^&*()\-_=+{};:,<.>])/,
              "Must contain at least one special character"
            ),
      phone: Yup.string()
        .required("Phone is required")
        .test(
          "isValidPhone",
          "Invalid phone number format",
          (value) => !value || isValidPhoneNumber(value)
        ),
      role: Yup.array(),
      companyName: Yup.string().matches(
        charactersWithSpaces,
        "company name should contain characters only"
      ),
      status: Yup.string(),
      description: Yup.string(),
    }),

    onSubmit: async (values) => {
      try {
        const payload = {
          employeeId: values.employeeId,
          subscriptionId: values.subscriptionId,
          firstName: values.firstname,
          lastName: values.lastname,
          userName: values.username,
          password: userId
            ? values.password
              ? values.password
              : undefined
            : values.password, // Do not send password if editing
          email: values.email,
          phoneNumber: values.phone,
          rolePermissions: values.role,
          companyName: values.companyName,
          status: values.status,
          description: values.description,
        };

        if (userId) {
          await put(api.UPDATE_USERDATA(userId), payload);
          showSuccessToast("User data updated successfully");
        } else {
          await post(api.CREATE_USER, payload);
          showSuccessToast("User created successfully");
        }

        navigate("/users");
      } catch (error) {
        console.error("Error submitting form:", error);
        showErrorToast("Something went wrong");
      }
    },
  });

  const handleInput = (e) => {
    e.target.value = e.target.value.replace(/[^0-9]/g, "");
  };

  return (
    <>
      <div className="page-content">
        <Container fluid={true} style={{ padding: "0px", gap: "10px" }}>
          <PageSubHeader
            heading={userId ? "Edit User" : "Add User"}
            routePath={"Home"}
            pageName={userId ? "Edit User" : "Add User"}
          />
          <Row className="p-4">
            <Col>
              <Card>
                <CardBody>
                  <Form className="row g-3 needs-validation py-4">
                    <Row>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="employeeId">Employee</Label>
                          <Input
                            name="employeeId"
                            type="select"
                            className="form-select"
                            id="employeeId"
                            onChange={handleEmployeeChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.employeeId || ""}
                            invalid={
                              validation.touched.employeeId &&
                              validation.errors.employeeId
                            }
                          >
                            <option value="">Select Employee</option>
                            {employees.map((data, id) => (
                              <option key={id} value={data.value}>
                                {data.label}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.employeeId &&
                            validation.errors.employeeId && (
                              <FormFeedback type="invalid">
                                {validation.errors.employeeId}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="subscriptionId">
                            Subscription Id
                          </Label>
                          <Input
                            name="subscriptionId"
                            placeholder=""
                            type="text"
                            className="form-control"
                            id="subscriptionId"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.subscriptionId || ""}
                            invalid={
                              validation.touched.subscriptionId &&
                              validation.errors.subscriptionId
                                ? true
                                : false
                            }
                          />
                          {validation.touched.subscriptionId &&
                          validation.errors.subscriptionId ? (
                            <FormFeedback type="invalid">
                              {validation.errors.subscriptionId}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="validationCustom01">
                            First name <span className="text-danger">*</span>
                          </Label>
                          <CustomInput
                            name={"firstname"}
                            placeholder={""}
                            id="validationCustom01"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.firstname || ""}
                            invalid={
                              validation.touched.firstname &&
                              validation.errors.firstname
                                ? true
                                : false
                            }
                          />
                          {validation.touched.firstname &&
                          validation.errors.firstname ? (
                            <FormFeedback type="invalid">
                              {validation.errors.firstname}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="validationCustom02">
                            Last name <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="lastname"
                            placeholder=""
                            type="text"
                            className="form-control"
                            id="validationCustom02"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.lastname || ""}
                            invalid={
                              validation.touched.lastname &&
                              validation.errors.lastname
                                ? true
                                : false
                            }
                          />
                          {validation.touched.lastname &&
                          validation.errors.lastname ? (
                            <FormFeedback type="invalid">
                              {validation.errors.lastname}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="validationCustom06">
                            User Name <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="username"
                            placeholder=""
                            type="text"
                            className="form-control"
                            id="validationCustom02"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.username || ""}
                            invalid={
                              validation.touched.username &&
                              validation.errors.username
                                ? true
                                : false
                            }
                          />
                          {validation.touched.username &&
                          validation.errors.username ? (
                            <FormFeedback type="invalid">
                              {validation.errors.username}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="validationCustom03">
                            Email <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="email"
                            placeholder=""
                            type="email"
                            className="form-control"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email || ""}
                            invalid={
                              validation.touched.email &&
                              validation.errors.email
                                ? true
                                : false
                            }
                          />
                          {validation.touched.email &&
                          validation.errors.email ? (
                            <FormFeedback type="invalid">
                              {validation.errors.email}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="validationCustom04">
                            Password{" "}
                            {userId ? null : (
                              <span className="text-danger">*</span>
                            )}
                          </Label>
                          <Input
                            name="password"
                            placeholder=""
                            type="text"
                            className="form-control"
                            id="validationCustom04"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.password || ""}
                            invalid={
                              validation.touched.password &&
                              validation.errors.password
                                ? true
                                : false
                            }
                          />
                          {validation.touched.password &&
                          validation.errors.password ? (
                            <FormFeedback type="invalid">
                              {validation.errors.password}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="validationCustom04">
                            Phone number <span className="text-danger">*</span>
                          </Label>
                          <PhoneInput
                            international
                            defaultCountry="IN"
                            name="phone"
                            placeholder=""
                            type="text"
                            className="form-control d-flex"
                            id="validationCustom04"
                            onChange={(e) => {
                              validation.setFieldValue("phone", e);
                            }}
                            onBlur={validation.handleBlur}
                            value={validation.values.phone || ""}
                            onInput={handleInput}
                            invalid={
                              validation.touched.phone &&
                              validation.errors.phone
                                ? true
                                : false
                            }
                          />
                          {validation.touched.phone &&
                          validation.errors.phone ? (
                            <div className="phonenumber-invalid">
                              {validation.errors.phone}
                            </div>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label className="control-label">Role</Label>
                          <Select
                            isMulti
                            name="role"
                            options={roleSelect}
                            value={roleSelect.filter((roleOption) =>
                              validation.values.role.includes(roleOption.value)
                            )}
                            onChange={(selected) => {
                              const selectedRoles = selected
                                ? selected.map((item) => item.value)
                                : [];
                              validation.setFieldValue("role", selectedRoles);
                            }}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            menuPortalTarget={document.body}
                            maxMenuHeight={200}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              menu: (base) => ({
                                ...base,
                                maxHeight: 200,
                                overflowY: "auto",
                              }),
                            }}
                          />

                          {validation.touched.role && validation.errors.role ? (
                            <FormFeedback type="invalid">
                              {validation.errors.role}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="validationCustom03">
                            Company Name
                          </Label>
                          <Input
                            name="companyName"
                            placeholder=""
                            type="text"
                            className="form-control"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.companyName || ""}
                            invalid={
                              validation.touched.companyName &&
                              validation.errors.companyName
                                ? true
                                : false
                            }
                          />
                          {validation.touched.companyName &&
                          validation.errors.companyName ? (
                            <FormFeedback type="invalid">
                              {validation.errors.companyName}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="status">Status</Label>
                          <Input
                            type="select"
                            id="status"
                            name="status"
                            className="form-select"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.status || ""}
                            invalid={
                              validation.touched.status &&
                              validation.errors.status
                                ? true
                                : false
                            }
                          >
                            <option value={1}>Active</option>
                            <option value={0}>Inactive</option>
                          </Input>
                          {validation.touched.status &&
                          validation.errors.status ? (
                            <FormFeedback type="invalid">
                              {validation.errors.status}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="validationCustom03">
                            Description
                          </Label>
                          <Input
                            name="description"
                            placeholder=""
                            type="text"
                            className="form-control"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.description || ""}
                            invalid={
                              validation.touched.description &&
                              validation.errors.description
                                ? true
                                : false
                            }
                          />
                          {validation.touched.description &&
                          validation.errors.description ? (
                            <FormFeedback type="invalid">
                              {validation.errors.description}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
        <Footer save={validation.handleSubmit} />
      </div>
    </>
  );
}

export default AddUser;
